import { useNavigate, useParams } from "react-router-dom";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { isFetchableViewId, resolveBlockRef } from "@tellescope/contact-details";
import { useAppPermissionQuery, useOrgFeatures } from "../../permissions";
import { useCalendarShallowStore } from "../../store";
import { buildCalendarPath } from "../calendar/build-calendar-path";
import { buildTakeoverPath } from "./takeover-path";

export const useContactDetailsTakeoverPage = () => {
  const navigate = useNavigate();
  const { enduserId, viewId, blockId } = useParams();
  const { isFeatureEnabled } = useOrgFeatures();
  const { update: updateObservationsPermission } = useAppPermissionQuery({
    model: "enduser_observations",
    actions: ["update"],
  });
  const { lastSelectedViewType, lastSelectedViewId } = useCalendarShallowStore(
    "lastSelectedViewType",
    "lastSelectedViewId"
  );

  const blockQuery = useTellescopeQuery({
    model: "custom_dashboards",
    method: "getOne",
    request: viewId ?? "",
    enabled: isFetchableViewId(viewId),
    select: (data) => resolveBlockRef({ blocks: data.blocks, blockId, blockIndex: -1 }),
  });

  const block = blockQuery.data?.block;

  const isUnresolvable = !enduserId || !viewId || !blockId || (!blockQuery.isPending && !block);

  const onClose = () => navigate(-1);

  const onOpenTicket = (ticketId: string) => ({ to: `/tickets/details/${ticketId}` });

  const onOpenEvent = (eventId: string, startTimeInMS: number) => ({
    to: `${buildCalendarPath({
      viewType: lastSelectedViewType,
      fallbackViewType: lastSelectedViewType,
      fallbackViewId: lastSelectedViewId,
      date: new Date(startTimeInMS),
    })}?eventId=${eventId}`,
  });

  const onChangeEnduser = (nextEnduserId: string) => {
    if (!viewId || !blockId) return;
    navigate(buildTakeoverPath({ enduserId: nextEnduserId, viewId, blockId }), { replace: true });
  };

  return {
    block,
    resolvedBlockIndex: blockQuery.data?.index ?? -1,
    blockId,
    viewId,
    enduserId,
    isUnresolvable,
    isFeatureEnabled,
    canUpdateObservations: updateObservationsPermission.access,
    onClose,
    onOpenTicket,
    onOpenEvent,
    onChangeEnduser,
  };
};
